import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { SatDatepickerModule, SatNativeDateModule } from "saturn-datepicker";
import { AppRoutingModule } from "./app-routing.module";

// Flex
import { FlexLayoutModule } from "@angular/flex-layout";

// Angular Material
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MaterialModule } from "./material.module";

// NGXS
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsModule } from "@ngxs/store";
import { AppState } from "./app.state";
import { ErrorState } from "./components/errors/state/error.state";
import { LogState } from "./log.state";
import { AssetState } from "./views/asset-details/state/asset-details.state";
import { DefectListState } from "./views/defect-list/state/defect-list.state";
import { InspectionState } from "./views/inspection-detail/state/inspection-detail.state";
import { InspectionHistoryState } from "./views/inspection-history/state/inspection-history.state";
import { MissingInspectionsState } from "./views/missing-inspections/state/missing-inspection.state";
import { RepairState } from "./views/repairs/state/repairs.state";

// Auth0
import { AuthHttpInterceptor, AuthModule } from "@auth0/auth0-angular";

// Router Services
import { ErrorService } from "./components/errors/service/error.service";

// I18Next
import { I18NextModule } from "angular-i18next";
import { I18N_PROVIDERS } from "./i18next";

// Environment
import { environment } from "src/environments/environment";

// Components
import { A11yModule } from "@angular/cdk/a11y";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { HashLocationStrategy, LocationStrategy } from "@angular/common";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import {
	AuthInterceptor,
	PXInitService,
	ZonarOwnerIdInterceptor,
	ZonarUiAuthModule,
	ZonarUiPermissionsModule,
} from "@zonar-ui/auth";
import { ZonarUICoreModule } from "@zonar-ui/core";
import { ZonarUiFooterModule } from "@zonar-ui/footer";
import { TranslateLoader, TranslateModule, ZonarUiI18nModule } from "@zonar-ui/i18n";
import { ZonarUIMaterialModule } from "@zonar-ui/material";
import { SidenavModule as ZonarUiSidenavModule } from "@zonar-ui/sidenav";
import { AppComponent } from "./app.component";

import { MatSliderModule } from "@angular/material/slider";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ActionButtonComponent } from "./components/action-button/action-button.component";
import { AssetFilterComponent } from "./components/asset-filter/asset-filter.component";
import { AssignRuleComponent } from "./components/assign-rule/assign-rule.component";
import { CharacterCounterComponent } from "./components/character-counter/character-counter.component";
import { CircleButtonComponent } from "./components/circle-button/circle-button.component";
import { CompanyAddressInputComponent } from "./components/company-address-input/company-address-input.component";
import { ConfirmActionBtnComponent } from "./components/confirm-action-btn/confirm-action-btn.component";
import { ConfirmClearRuleComponent } from "./components/confirm-clear-rule/confirm-clear-rule.component";
import { ConfirmDeleteRuleDialogComponent } from "./components/confirm-delete-rule-dialog/confirm-delete-rule-dialog.component";
import { ConfirmDivisionChangeComponent } from "./components/confirm-division-change/confirm-division-change.component";
import { ConfirmEditRuleDialogComponent } from "./components/confirm-edit-rule-dialog/confirm-edit-rule-dialog.component";
import { ControlBoxComponent } from "./components/control-box/control-box.component";
import { CustomCalendarHeaderComponent } from "./components/custom-calendar-header/custom-calendar-header.component";
import { CustomDateRangeFilterComponent } from "./components/custom-date-range-filter/custom-date-range-filter.component";
import { DateRangeFilterComponent } from "./components/date-range-filter/date-range-filter.component";
import { DefectFilterComponent } from "./components/defect-type-filter/defect-type-filter.component";
import { DivisionFilterV2Component } from "./components/division-filter-v2/division-filter-v2.component";
import { DivisionFilterComponent } from "./components/division-filter/division-filter.component";
import { ErrorsComponent } from "./components/errors/errors.component";
import { ExportComponent } from "./components/export/export/export.component";
import { FilterCardComponent } from "./components/filter-card/filter-card.component";
import { GetTableFilterComponent } from "./components/get-table-filter/get-table-filter.component";
import { HighlightedTextComponent } from "./components/highlighted-text/highlighted-text.component";
import { ImageCarouselDialogComponent } from "./components/image-carousel-dialog/image-carousel-dialog.component";
import { InfoDialogComponent } from "./components/info-dialog/info-dialog.component";
import { MissingInputComponent } from "./components/missing-input/missing-input.component";
import { MultiSelectChipsComponent } from "./components/multi-select-chips/multi-select-chips.component";
import { MultiSelectDropdownComponent } from "./components/multi-select-dropdown/multi-select-dropdown.component";
import { NjmvcExportDialogComponent } from "./components/njmvc-export-dialog/njmvc-export-dialog.component";
import { NoResultsCardComponent } from "./components/no-results-card/no-results-card.component";
import { OverlayComponent } from "./components/overlay/overlay.component";
import { PaginatorComponent } from "./components/paginator/paginator.component";
import { PdfExportDialogComponent } from "./components/pdf-export-dialog/pdf-export-dialog.component";
import { ProgressDialogComponent } from "./components/progress-dialog/progress-dialog.component";
import { RuleFilterComponent } from "./components/rule-filter/rule-filter.component";
import { RuleSummaryComponent } from "./components/rule-summary/rule-summary.component";
import { SearchInputComponent } from "./components/search-input/search-input.component";
import { SeverityZoneIconComponent } from "./components/severity-zone-icon/severity-zone-icon.component";
import { SimpleTabsComponent } from "./components/simple-tabs/simple-tabs.component";
import { SingleSelectDropdownComponent } from "./components/single-select-dropdown/single-select-dropdown.component";
import { TabControlComponent } from "./components/tab-control/tab-control.component";
import { TabComponent } from "./components/tab/tab.component";
import { ColumnComponent } from "./components/table/column/column.component";
import { TableComponent } from "./components/table/table.component";
import { TimeDurationSelectorComponent } from "./components/time-duration-selector/time-duration-selector.component";
import { TimePickerComponent } from "./components/time-picker/time-picker.component";
import { TimeoutDialogComponent } from "./components/timeout-dialog/timeout-dialog.component";
import { TripStartEndComponent } from "./components/trip-start-end/trip-start-end.component";
import { VehicleTypeFilterComponent } from "./components/vehicle-type-filter/vehicle-type-filter.component";
import { WizardStepComponent } from "./components/wizard/components/wizard-step/wizard-step.component";
import { WizardComponent } from "./components/wizard/wizard.component";
import { DefineDutyEndComponent } from "./components/wizard/wizards/create-rule/define-duty-end/define-duty-end.component";
import { DefineDutyComponent } from "./components/wizard/wizards/create-rule/define-duty/define-duty.component";
import { GetStartedComponent } from "./components/wizard/wizards/create-rule/get-started/get-started.component";
import { ReviewAndSubmitComponent } from "./components/wizard/wizards/create-rule/review-and-submit/review-and-submit.component";
import { ZonesSelectorComponent } from "./components/zones-selector/zones-selector.component";
import { permissions } from "./constants/internal-constants";
import { GenericLanguageMap } from "./i18next/generic-language.map";
import { LocalizedNumberPipe } from "./pipe/localized-number.pipe";
import { DisplayTimezonePipe } from "./pipes/display-timezone.pipe";
import { SplitAtFirstDelimiterPipe } from "./pipes/split-at-first-delimiter.pipe";
import { LocalStateService } from "./services/local-state/local-state.service";
import { AssetDetailsComponent } from "./views/asset-details/asset-details.component";
import { ClosedIgnoredComponent } from "./views/asset-details/closed-ignored/closed-ignored.component";
import { OpenPendingComponent } from "./views/asset-details/open-pending/open-pending.component";
import { DefaultLayoutComponent } from "./views/default-layout/default-layout.component";
import { DefectListComponent } from "./views/defect-list/defect-list.component";
import { InspectionDetailComponent } from "./views/inspection-detail/inspection-detail.component";
import { InspectionListViewComponent } from "./views/inspection-detail/inspection-list-view/inspection-list-view.component";
import { InspectionHistoryComponent } from "./views/inspection-history/inspection-history.component";
import { InspectionsPrintViewComponent } from "./views/inspections-print-view/inspections-print-view.component";
import { MirRulesComponent } from "./views/mir-rules/mir-rules.component";
import { AssetsTableComponent } from "./views/mir-rules/tables/assets-table/assets-table.component";
import { RulesTableComponent } from "./views/mir-rules/tables/rules-table/rules-table.component";
import { MissingInspectionsComponent } from "./views/missing-inspections/missing-inspections.component";
import { NjmvcDriversVehicleInspectionReportComponent } from "./views/njmvc-drivers-vehicle-inspection-report/njmvc-drivers-vehicle-inspection-report.component";
import { RepairModalComponent } from "./views/repairs/modal/repair-modal.component";
import { RepairButtonsComponent } from "./views/repairs/repair-buttons/repair-buttons.component";
import { SettingsComponent } from "./views/settings/settings.component";
import { SingleNjmvcDriversVehicleInspectionReportComponent } from "./views/single-njmvc-drivers-vehicle-inspection-report/single-njmvc-drivers-vehicle-inspection-report.component";
export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http);
}

export function startupFactory(initService: PXInitService, localStateService: LocalStateService) {
	return () =>
		initService.init().then(value => {
			localStateService.setPXLogin(value);
		});
}

@NgModule({
	declarations: [
		ActionButtonComponent,
		AppComponent,
		AssetDetailsComponent,
		AssetFilterComponent,
		AssignRuleComponent,
		CharacterCounterComponent,
		CircleButtonComponent,
		ClosedIgnoredComponent,
		ColumnComponent,
		CompanyAddressInputComponent,
		ConfirmClearRuleComponent,
		ConfirmActionBtnComponent,
		ConfirmDeleteRuleDialogComponent,
		ConfirmDivisionChangeComponent,
		ConfirmEditRuleDialogComponent,
		ControlBoxComponent,
		CustomCalendarHeaderComponent,
		CustomDateRangeFilterComponent,
		DateRangeFilterComponent,
		DefaultLayoutComponent,
		DefectFilterComponent,
		DefectListComponent,
		DefineDutyEndComponent,
		DefineDutyComponent,
		DivisionFilterComponent,
		DivisionFilterV2Component,
		ErrorsComponent,
		ExportComponent,
		FilterCardComponent,
		GetStartedComponent,
		GetTableFilterComponent,
		HighlightedTextComponent,
		ImageCarouselDialogComponent,
		InfoDialogComponent,
		InspectionDetailComponent,
		InspectionHistoryComponent,
		InspectionListViewComponent,
		InspectionsPrintViewComponent,
		LocalizedNumberPipe,
		LocalizedNumberPipe,
		MissingInputComponent,
		MissingInspectionsComponent,
		MultiSelectDropdownComponent,
		MultiSelectChipsComponent,
		TimeDurationSelectorComponent,
		SingleSelectDropdownComponent,
		MirRulesComponent,
		SingleNjmvcDriversVehicleInspectionReportComponent,
		NjmvcDriversVehicleInspectionReportComponent,
		NjmvcExportDialogComponent,
		NoResultsCardComponent,
		OpenPendingComponent,
		OverlayComponent,
		PaginatorComponent,
		PdfExportDialogComponent,
		RepairButtonsComponent,
		RepairModalComponent,
		RuleFilterComponent,
		ReviewAndSubmitComponent,
		RuleSummaryComponent,
		ReviewAndSubmitComponent,
		SearchInputComponent,
		SettingsComponent,
		SeverityZoneIconComponent,
		SimpleTabsComponent,
		SplitAtFirstDelimiterPipe,
		TabComponent,
		TabControlComponent,
		TableComponent,
		TimeoutDialogComponent,
		TimePickerComponent,
		TripStartEndComponent,
		VehicleTypeFilterComponent,
		RulesTableComponent,
		AssetsTableComponent,
		WizardComponent,
		WizardStepComponent,
		ZonesSelectorComponent,
		ProgressDialogComponent,
		DisplayTimezonePipe,
	],

	imports: [
		BrowserModule,
		HttpClientModule,
		AppRoutingModule,
		MaterialModule,
		BrowserAnimationsModule,
		FormsModule,
		ReactiveFormsModule,
		A11yModule,
		ScrollingModule,
		MatSliderModule,
		NgxsModule.forRoot([
			DefectListState,
			InspectionHistoryState,
			InspectionState,
			AppState,
			AssetState,
			RepairState,
			MissingInspectionsState,
			ErrorState,
			LogState,
		]),
		NgxsReduxDevtoolsPluginModule.forRoot(),
		NgxSkeletonLoaderModule,
		SatDatepickerModule,
		SatNativeDateModule,
		FlexLayoutModule,
		I18NextModule.forRoot(),
		// pattern library
		AuthModule.forRoot({
			clientId: environment.auth0.CLIENT_ID,
			domain: environment.auth0.DOMAIN,
			audience: environment.auth0.AUDIENCE,
			redirect_uri: window.location.origin + "/inspection-list",
			scope: "openid profile email",
			useRefreshTokens: true,
			max_age: 36000,
			cacheLocation: "localstorage",
			httpInterceptor: {
				allowedList: [
					// core entity api base urls. these are used by the auth module to make calls to the user-api
					`${environment.environmentConstants.APP_ENDPOINT_CORE_BASE}/v1/*`,
					`${environment.environmentConstants.APP_ENDPOINT_CORE_BASE}/v1beta2/*`,
					`${environment.environmentConstants.APP_ENDPOINT_EVIR_V2}/*`,
				],
			},
		}),
		ZonarUiAuthModule,
		ZonarUiPermissionsModule,
		ZonarUiSidenavModule,
		ZonarUICoreModule,
		ZonarUiFooterModule,
		ZonarUIMaterialModule,
		ZonarUiI18nModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
		}),
	],
	providers: [
		{
			provide: APP_INITIALIZER,
			useFactory: startupFactory,
			deps: [PXInitService, LocalStateService],
			multi: true,
		},
		{
			provide: ErrorHandler,
			useClass: ErrorService,
		},
		I18N_PROVIDERS,
		{ provide: "applicationId", useValue: environment.environmentConstants.APP_APPLICATION_ID },
		{ provide: "defaultLanguage", useValue: environment.i18n.defaultLanguage },
		{ provide: "defaultPermPrefix", useValue: permissions.defaultPermPrefix },
		{ provide: "environment", useValue: environment.environment },
		{ provide: "genericLanguageMap", useValue: GenericLanguageMap },
		{ provide: "logoutReturnToUrl", useValue: environment.environmentConstants.APP_URL },
		{ provide: "pendoParams", useValue: environment.pendo },
		{ provide: "redirectUrl", useValue: permissions.defaultRedirectUrl },
		{ provide: "region", useValue: environment.region },
		{ provide: "routePermissions", useValue: permissions.routes },
		{ provide: "supportedLanguages", useValue: environment.i18n.supportedLanguages },
		{ provide: "useBrowserLanguage", useValue: true },
		{
			provide: "allowedOrigins",
			useValue: [
				"https://advantage.gpstrackit.com",
				"https://assuredtechmatics.gpstrackit.com",
				"https://cloud.fleetiq.com",
				"https://cloud.genesiscarconcepts.com",
				"https://cloud.gpsmytruck.com",
				"https://cloud.gpsoftexas.com",
				"https://cloud.gpstrackit.com",
				"https://cloud.monarchtracking.com",
				"https://cloud.motustrack.com",
				"https://cloud.pureconnectgps.com",
				"https://cloud.zonar.com",
				"https://cloud.zonarsystems.com",
				"https://gps.metropark.com",
				"https://gps.southerntechpartners.net",
				"https://gpsoftexas.gpstrackit.com",
				"https://gpstrackit.com",
				"https://ignition.gpstrackit.com",
				"https://ignition.zonar.com",
				"https://ignition.zonarsystems.com",
				"https://reseller1.gpstrackit.com",
				"https://scr.gpstrackit.com",
				"https://southerntechpartners.gpstrackit.com",
				"https://track.connectidcrm.com",
				"http://localhost:40000",
				"http://localhost:4200",
			],
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass:
				new URLSearchParams(window.location.search).get("pxLogin")?.toLocaleLowerCase() === "true"
					? AuthInterceptor
					: AuthHttpInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ZonarOwnerIdInterceptor,
			multi: true,
		},
		{ provide: LocationStrategy, useClass: HashLocationStrategy },
		{
			provide: "options",
			useValue: {
				zonarOwnerIdInterceptor: {
					requiresZonarOwnerId: ["https://evir-api-v2.staging.production.zonarsystems.net"],
				},
			},
		},
		{ provide: "devCycleClientId", useValue: environment.devCycle.CLIENT_ID },
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor() {}
}
